import { FC } from 'react';

import css from 'components/Admin/AttendanceDetails.module.scss';
import Dialog from 'components/Dialog';
import scss from 'components/shared.module.scss';
import { Button, LoaderSpinner } from 'components/UtilElements';
import { useStudentAnalyticsAPI } from 'services/course-api';

const getColorForAttendance = (percentage: number) => {
  if (percentage >= 80) return scss['data--good'];
  else if (percentage >= 60) return scss['data--ok'];
  else return scss['data--bad'];
};

const getColorForInsights = (rating: InsightRating) => {
  switch (rating) {
    case 'vhigh':
    case 'high':
      return scss['data--good'];
    case 'okay':
      return scss['data--ok'];
    case 'na':
      return scss['data--na'];
    default:
      return scss['data--bad'];
  }
};

const getInsightValue = (rating: InsightRating) => {
  switch (rating) {
    case 'vhigh':
      return 'Very High';
    case 'high':
      return 'High';
    case 'okay':
      return 'Okay';
    case 'low':
      return 'Low';
    case 'vlow':
      return 'Very Low';
    default:
      return 'NA';
  }
};

interface CourseInsightsTableProps {
  course: StudentCourseInsights;
}

const CourseInsightsTable: FC<CourseInsightsTableProps> = ({ course }) => {
  return (
    <div className={css['class-attendance-details-body']}>
      <div className={css.header}>
        <div className={css['header-row']}>
          {course.courseCode} - {course.title}
        </div>
        <div className={css['header-row']}>{course.admin.name}</div>
      </div>
      {course.available ? (
        <p className={css['empty-message']}>{course.unavailableMessage}</p>
      ) : (
        <table className={css['attendance-table']}>
          <thead>
            <tr>
              <th>Week No.</th>
              <th>Engagement</th>
              <th>Regularity</th>
              <th>Participation</th>
              <th>Preparedness</th>
              <th>Performance</th>
              <th>Attendance</th>
            </tr>
          </thead>
          <tbody>
            {course.weeks.map((w, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td className={getColorForInsights(w.eng)}>{getInsightValue(w.eng)}</td>
                <td className={getColorForInsights(w.reg)}>{getInsightValue(w.reg)}</td>
                <td className={getColorForInsights(w.par)}>{getInsightValue(w.par)}</td>
                <td className={getColorForInsights(w.ppd)}>{getInsightValue(w.ppd)}</td>
                <td className={getColorForInsights(w.per)}>{getInsightValue(w.per)}</td>
                <td className={getColorForAttendance(w.att)}>{w.att}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

interface StudentAnalyticsDetailsProps {
  isOpen: boolean;
  studentId: string;
  onClose(): void;
}

const StudentAnalyticsDetails = ({ isOpen, onClose, studentId }: StudentAnalyticsDetailsProps) => {
  const { isLoading, data: student } = useStudentAnalyticsAPI(studentId);
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className={css['class-attendance-details']}>
          <div className={css.number}>{student?.name}</div>
          <div className={css.title}>{student?.emailId}</div>
        </div>
      }
      footer={
        <Button variant='text' color='success' size='small' onClick={onClose} preventHoverStyles>
          DONE
        </Button>
      }
    >
      {(student === null || isLoading) && (
        <div className={scss['loader-wrapper']}>
          <LoaderSpinner />
        </div>
      )}
      {student?.insights.map((course) => (
        <CourseInsightsTable key={course.courseId} course={course} />
      ))}
    </Dialog>
  );
};

export default StudentAnalyticsDetails;
